/**
 * CameraWithLayoutOverlay — live camera viewfinder with semi-transparent
 * board-layout frame overlays.
 *
 * The engineer picks a saved board layout, opens this dialog, and lines
 * up the real distribution board with the on-screen frames before
 * tapping "Capture". The captured frame is uploaded via uploadStaging
 * and the staged photo metadata is returned via `onCaptured`.
 *
 * Why getUserMedia rather than `<input capture>`?
 * The native camera UI doesn't let us draw an overlay. We need the
 * frames visible WHILE the engineer aims the phone, so we run our own
 * <video> + transparent <div> overlay layer.
 */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Camera, X, RotateCcw, Check, Loader2, ZoomIn, ZoomOut,
  Plus, Minus, ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { uploadStaging } from '../services/api';

const FRAME_COLOURS = {
  breaker: 'rgba(59,130,246,0.5)',       // blue
  isolator: 'rgba(234,179,8,0.55)',      // amber
  circuit_label: 'rgba(16,185,129,0.5)', // emerald
};

const FRAME_BORDER = {
  breaker: 'rgb(59,130,246)',
  isolator: 'rgb(234,179,8)',
  circuit_label: 'rgb(16,185,129)',
};

/**
 * Translate the layout's frames by the engineer's interactive zoom +
 * pan adjustments. Used both for the live preview transform AND for
 * computing the FINAL frame coordinates that travel with the photo
 * to the AI extractor — so GPT sees what the engineer actually
 * aligned, not the layout's pristine coords.
 *
 * Math mirrors the CSS `translate(pan.x%, pan.y%) scale(scale)` we
 * apply to the overlay wrapper: scale around (50, 50), then pan.
 */
const applyTransformToFrames = (frames, scale, pan) =>
  (frames || []).map((f) => {
    const cx = f.x + f.w / 2;
    const cy = f.y + f.h / 2;
    const nCx = 50 + (cx - 50) * scale + pan.x;
    const nCy = 50 + (cy - 50) * scale + pan.y;
    const nW = f.w * scale;
    const nH = f.h * scale;
    return { ...f, x: nCx - nW / 2, y: nCy - nH / 2, w: nW, h: nH };
  });

const CameraWithLayoutOverlay = ({
  open,
  onOpenChange,
  layout,
  onCaptured,
  // When `initialPhoto` is provided we skip the live camera entirely
  // and drop straight into the preview/adjust state with that photo as
  // the background. Used by the "Align overlay on this photo" flow on
  // already-uploaded shots — same pinch/pan + frameOverrides plumbing,
  // no re-upload.
  //   { url: string, storedAs?: string, adjustedFrames?: array }
  initialPhoto = null,
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null); // captured snapshot dataURL
  const [error, setError] = useState(null);
  const [overlayOpacity, setOverlayOpacity] = useState(0.55);
  // Independent zoom for the frame overlay only — the photo / video
  // never moves. Lets the engineer expand or shrink the frame grid to
  // match boards that don't perfectly match the original layout's
  // proportions. Frames scale around the canvas centre via CSS
  // transform, so this is purely a visual guide and doesn't change
  // the layout's saved coordinates (the AI still gets the original
  // % positions on extract).
  const [frameScale, setFrameScale] = useState(1.0);
  // Pan offset in % of canvas, so engineers can nudge the whole
  // frame grid up/down/left/right after zooming.
  const [framePan, setFramePan] = useState({ x: 0, y: 0 });
  // Start / stop the camera stream when the dialog opens / closes.
  // When `initialPhoto` is supplied we skip the live camera entirely
  // and jump straight into preview/adjust mode using that photo.
  useEffect(() => {
    if (!open) {
      stopCamera();
      setPreviewUrl(null);
      setError(null);
      setFrameScale(1.0);
      setFramePan({ x: 0, y: 0 });
      return;
    }
    if (initialPhoto?.url) {
      setPreviewUrl(initialPhoto.url);
      setFrameScale(1.0);
      setFramePan({ x: 0, y: 0 });
      return undefined;
    }
    // Live-camera flow.
    startCamera();
    return stopCamera;
    // eslint-disable-next-line
  }, [open, initialPhoto?.url]);

  const startCamera = async () => {
    setError(null);
    try {
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      // The <video> element is rendered conditionally on `previewUrl`,
      // so the very first call after `open` can race the React commit
      // and find videoRef.current === null. A short retry catches that
      // window without spamming requestAnimationFrame.
      const attach = async () => {
        const v = videoRef.current;
        if (!v) return false;
        v.srcObject = stream;
        // Safari + some Android browsers ignore `autoPlay` on srcObject
        // assignments made async after mount; calling play() explicitly
        // is the documented MDN pattern. Wrap in try/catch — autoplay
        // may still be blocked, but we log it instead of swallowing.
        try { await v.play(); } catch (e) { /* eslint-disable-next-line no-console */ console.warn('camera video.play() blocked:', e?.name || e); }
        return true;
      };
      if (!(await attach())) {
        // One retry on the next tick to cover the mount-race case.
        await new Promise((r) => setTimeout(r, 60));
        if (!(await attach())) {
          throw new Error('Camera element not ready');
        }
      }
    } catch (e) {
      // Common cases: permission denied, no rear camera, HTTPS-only.
      setError(
        e?.name === 'NotAllowedError'
          ? 'Camera permission denied. Allow camera access in your browser settings, then retry.'
          : e?.message || 'Could not start the camera.'
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const capture = () => {
    if (!videoRef.current || !canvasRef.current) return;
    setCapturing(true);
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      // The live <video> uses `object-cover` to fill the 4:3 container,
      // which crops the sides (or top/bottom) of the raw stream. The
      // captured snapshot must match what the engineer actually saw —
      // otherwise the frame overlays will sit at the same % of the
      // container while the underlying photo geometry shifts, putting
      // every frame in the wrong place. So we crop the source video
      // to the same visible region the user lined up against.
      const containerEl = video.parentElement;
      const cw = containerEl?.clientWidth || video.clientWidth || 1280;
      const ch = containerEl?.clientHeight || video.clientHeight || 960;
      const vw = video.videoWidth || 1280;
      const vh = video.videoHeight || 720;
      const containerAR = cw / ch;
      const videoAR = vw / vh;
      let sx, sy, sw, sh;
      if (videoAR > containerAR) {
        // Video is wider than container — sides get cropped by object-cover
        sh = vh;
        sw = vh * containerAR;
        sx = (vw - sw) / 2;
        sy = 0;
      } else {
        // Video is taller — top/bottom get cropped
        sw = vw;
        sh = vw / containerAR;
        sx = 0;
        sy = (vh - sh) / 2;
      }
      canvas.width = Math.round(sw);
      canvas.height = Math.round(sh);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setPreviewUrl(dataUrl);
      stopCamera();
    } catch (e) {
      toast.error('Could not capture frame: ' + (e.message || e));
    } finally {
      setCapturing(false);
    }
  };

  const retake = () => {
    setPreviewUrl(null);
    // Reset adjustments when retaking — the engineer is starting over.
    setFrameScale(1.0);
    setFramePan({ x: 0, y: 0 });
    startCamera();
  };

  // Move the frame grid by an exact pixel delta. Reads the overlay
  // box at click-time and converts the requested px → % so each
  // arrow click is a 1-pixel nudge regardless of dialog size /
  // current zoom level. (The dialog stores pan as % of the canvas
  // because the underlying transform uses CSS percentages.)
  const nudgeFramesPx = (dx, dy) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    const w = rect?.width || 766;
    const h = rect?.height || 575;
    const dxPct = (dx / w) * 100;
    const dyPct = (dy / h) * 100;
    setFramePan((p) => ({
      x: +Math.max(-50, Math.min(50, p.x + dxPct)).toFixed(3),
      y: +Math.max(-50, Math.min(50, p.y + dyPct)).toFixed(3),
    }));
  };

  const accept = async () => {
    if (!previewUrl) return;
    setBusy(true);
    try {
      // The frame source is whichever set the dialog opened on:
      // either a previously-saved alignment (resuming an edit) or the
      // layout's pristine frames (first-time alignment). Any pinch /
      // pan / zoom then composes ON TOP of that source so consecutive
      // edits compound naturally.
      const sourceFrames = (initialPhoto?.adjustedFrames?.length
        ? initialPhoto.adjustedFrames
        : layout?.frames) || [];
      const adjustedFrames = sourceFrames.length
        ? applyTransformToFrames(sourceFrames, frameScale, framePan)
        : [];
      if (initialPhoto) {
        // Adjust-existing mode — don't re-upload. Just hand the new
        // alignment back to the caller so it can patch the photo
        // record's `adjustedFrames` field.
        onCaptured && onCaptured(initialPhoto, { adjustedFrames, frameScale, framePan });
        toast.success('Frame alignment updated');
      } else {
        // Fresh-capture mode — upload the snapshot to staging.
        const res = await fetch(previewUrl);
        const blob = await res.blob();
        const file = new File([blob], `cert-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const meta = await uploadStaging(file);
        onCaptured && onCaptured(meta, { adjustedFrames, frameScale, framePan });
        toast.success('Photo captured with layout overlay');
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // ─── Two-finger pinch + drag for the overlay layer ───
  // Tracks up to two active touches. While both are down, the distance
  // between them drives `frameScale` and the centroid drift drives
  // `framePan` (in % of the canvas). One-finger touches are ignored so
  // we don't fight the browser's natural scroll.
  const overlayRef = useRef(null);
  const gestureRef = useRef(null);
  const onOverlayPointerDown = (e) => {
    if (!overlayRef.current) return;
    const g = gestureRef.current || (gestureRef.current = { pointers: new Map() });
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size === 2) {
      const [p1, p2] = Array.from(g.pointers.values());
      const rect = overlayRef.current.getBoundingClientRect();
      g.startDist = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
      g.startCenter = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };
      g.startScale = frameScale;
      g.startPan = { ...framePan };
      g.boxW = rect.width || 1;
      g.boxH = rect.height || 1;
      try { overlayRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      e.preventDefault();
    }
  };
  const onOverlayPointerMove = (e) => {
    const g = gestureRef.current;
    if (!g || g.pointers.size < 2 || !g.startDist) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const [p1, p2] = Array.from(g.pointers.values());
    const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
    const cx = (p1.x + p2.x) / 2;
    const cy = (p1.y + p2.y) / 2;
    const newScale = Math.max(0.3, Math.min(3.0, g.startScale * (dist / g.startDist)));
    const newPan = {
      x: Math.max(-50, Math.min(50, g.startPan.x + ((cx - g.startCenter.x) / g.boxW) * 100)),
      y: Math.max(-50, Math.min(50, g.startPan.y + ((cy - g.startCenter.y) / g.boxH) * 100)),
    };
    setFrameScale(+newScale.toFixed(3));
    setFramePan({ x: +newPan.x.toFixed(2), y: +newPan.y.toFixed(2) });
    e.preventDefault();
  };
  const onOverlayPointerUp = (e) => {
    const g = gestureRef.current;
    if (!g) return;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size < 2) g.startDist = 0;
    try { overlayRef.current?.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-slate-800">
        <DialogHeader className="px-4 pt-4 pb-2 bg-slate-900 text-white">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera className="h-5 w-5" />
            {initialPhoto
              ? `Align overlay — ${layout?.name || 'Board layout'}`
              : `Capture with overlay — ${layout?.name || 'Board layout'}`}
          </DialogTitle>
        </DialogHeader>

        <div
          className="relative bg-black w-full"
          style={{
            // Keep this in lock-step with the BoardLayoutEditor canvas
            // (also `aspect-ratio: 4 / 3` + `object-contain` photo).
            // Frame `%` coordinates are authored against THAT 4:3
            // canvas, so the dialog must mirror it exactly — otherwise
            // the frames would project onto a different physical area
            // of the photo. `object-contain` letterboxes portrait /
            // non-4:3 photos so the engineer never loses the edges.
            aspectRatio: '4 / 3',
          }}
          data-testid="camera-container"
        >
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white p-6">
              <X className="h-10 w-10 text-red-400 mb-3" />
              <p className="text-sm">{error}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={startCamera}>
                <RotateCcw className="h-4 w-4 mr-1" /> Retry
              </Button>
            </div>
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt="Captured"
              className="absolute inset-0 w-full h-full object-contain"
            />
          ) : (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}

          {/* Frame overlay — drawn on top of video OR captured photo.
              Wrapped in a transform layer so the engineer can zoom and
              pan the frame grid without touching the photo. The wrapper
              itself accepts pointer events for two-finger pinch-zoom +
              drag; inner frames keep `pointer-events: none` so children
              don't swallow the touches. When `initialPhoto` carries a
              previously-saved `adjustedFrames` array we render those
              instead of the layout's pristine frames so the engineer
              sees their earlier alignment as the starting point. */}
          {(() => {
            const overlayFrames = (initialPhoto?.adjustedFrames?.length
              ? initialPhoto.adjustedFrames
              : layout?.frames) || [];
            if (!overlayFrames.length || error) return null;
            return (
            <div
              ref={overlayRef}
              className="absolute inset-0"
              style={{ touchAction: 'none', cursor: 'move' }}
              onPointerDown={onOverlayPointerDown}
              onPointerMove={onOverlayPointerMove}
              onPointerUp={onOverlayPointerUp}
              onPointerCancel={onOverlayPointerUp}
              data-testid="camera-overlay-layer"
            >
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  transform: `translate(${framePan.x}%, ${framePan.y}%) scale(${frameScale})`,
                  transformOrigin: 'center center',
                  transition: gestureRef.current?.startDist ? 'none' : 'transform 80ms ease-out',
                }}
              >
                <FrameOverlay frames={overlayFrames} opacity={overlayOpacity} />
              </div>
              {previewUrl && (
                <div className="absolute top-2 left-2 right-2 text-center text-[11px] text-white/90 bg-slate-900/70 backdrop-blur-sm rounded px-2 py-1 pointer-events-none">
                  Pinch / drag with two fingers to fine-tune frames before saving
                </div>
              )}
            </div>
            );
          })()}

          {/* Hidden canvas used for the snapshot */}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        {/* Controls bar */}
        <div className="bg-slate-900 text-white px-4 py-3 space-y-2">
          {/* Top row: frame zoom (the user-requested control) + pan reset */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-400 w-20 shrink-0">Frame zoom</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-white hover:bg-slate-800"
              onClick={() => setFrameScale((s) => Math.max(0.3, +(s - 0.05).toFixed(2)))}
              title="Shrink frames"
              data-testid="frame-zoom-out-btn"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="font-mono text-slate-300 w-14 text-center" data-testid="frame-zoom-value">
              {Math.round(frameScale * 100)}%
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-white hover:bg-slate-800"
              onClick={() => setFrameScale((s) => Math.min(3.0, +(s + 0.05).toFixed(2)))}
              title="Grow frames"
              data-testid="frame-zoom-in-btn"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>

            {/* Pan controls — nudge frames after zooming. Each arrow
                click moves the frame grid by exactly 1 pixel of the
                current overlay (we read its rect at click-time and
                convert 1px → % of the box). The keyboard arrow keys
                bound to the dialog do the same nudge so engineers
                can fine-tune without picking the mouse back up. */}
            <div className="flex items-center gap-1 ml-2 border-l border-slate-700 pl-2">
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-slate-800"
                onClick={() => nudgeFramesPx(0, -1)} title="Nudge frames up 1 px"
                data-testid="frame-nudge-up-btn">
                <ChevronUp className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-slate-800"
                onClick={() => nudgeFramesPx(0, 1)} title="Nudge frames down 1 px"
                data-testid="frame-nudge-down-btn">
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-slate-800"
                onClick={() => nudgeFramesPx(-1, 0)} title="Nudge frames left 1 px"
                data-testid="frame-nudge-left-btn">
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-slate-800"
                onClick={() => nudgeFramesPx(1, 0)} title="Nudge frames right 1 px"
                data-testid="frame-nudge-right-btn">
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              {(frameScale !== 1 || framePan.x !== 0 || framePan.y !== 0) && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-slate-300 hover:bg-slate-800"
                  onClick={() => { setFrameScale(1); setFramePan({ x: 0, y: 0 }); }}
                  data-testid="frame-reset-btn"
                  title="Reset frame zoom and position">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                </Button>
              )}
            </div>

            {/* Opacity slider — kept for visibility tuning */}
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-slate-400">Opacity</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-slate-800"
                onClick={() => setOverlayOpacity((v) => Math.max(0.1, +(v - 0.15).toFixed(2)))} title="Lower overlay opacity">
                <ZoomOut className="h-3.5 w-3.5" />
              </Button>
              <span className="font-mono text-slate-300 w-10 text-center">{Math.round(overlayOpacity * 100)}%</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-white hover:bg-slate-800"
                onClick={() => setOverlayOpacity((v) => Math.min(0.95, +(v + 0.15).toFixed(2)))} title="Raise overlay opacity">
                <ZoomIn className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Bottom row: action buttons */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button variant="ghost" className="text-white hover:bg-slate-800" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
            {previewUrl ? (
              <>
                {!initialPhoto && (
                  <Button variant="outline" onClick={retake} className="text-slate-900" data-testid="camera-retake-btn">
                    <RotateCcw className="h-4 w-4 mr-1" /> Retake
                  </Button>
                )}
                <Button
                  onClick={accept}
                  disabled={busy}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="camera-accept-btn"
                >
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                  {initialPhoto ? 'Save alignment' : 'Use photo'}
                </Button>
              </>
            ) : (
              <Button
                onClick={capture}
                disabled={!!error || capturing}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="camera-capture-btn"
              >
                {capturing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />}
                Capture
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FrameOverlay = ({ frames, opacity, showLabels = false }) => (
  <div className="absolute inset-0 pointer-events-none">
    {frames.map((f) => (
      <div
        key={f.id}
        className="absolute"
        style={{
          left: `${f.x}%`,
          top: `${f.y}%`,
          width: `${f.w}%`,
          height: `${f.h}%`,
          backgroundColor: FRAME_COLOURS[f.type] || FRAME_COLOURS.breaker,
          border: `2px solid ${FRAME_BORDER[f.type] || FRAME_BORDER.breaker}`,
          opacity,
          mixBlendMode: 'screen',
        }}
      >
        {showLabels && f.label && (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow-md">
            {f.label}
          </div>
        )}
      </div>
    ))}
  </div>
);

// Allow other surfaces (review preview, etc.) to reuse just the overlay.
export { FrameOverlay };

export default CameraWithLayoutOverlay;
